import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { ErrorState, LoadingBlock } from "../components/Feedback";
import { ProgressBar } from "../components/Gates";
import { Icon } from "../components/Icon";
import { ApiError, api } from "../lib/api";
import { copyText } from "../lib/clipboard";
import { formatLongDate, pluralise } from "../lib/format";
import { useToast } from "../lib/toast";
import { useAsync } from "../lib/useAsync";

export default function Certificate() {
  const { courseId = "" } = useParams();
  const toast = useToast();
  const { data, loading, error, reload, set } = useAsync(
    (signal) => api.myCertificate(courseId, signal),
    [courseId],
  );
  const [claiming, setClaiming] = useState(false);
  const [copied, setCopied] = useState(false);

  const claim = async () => {
    setClaiming(true);
    try {
      const result = await api.claimCertificate(courseId);
      if (data) set({ ...data, certificate: result.certificate, eligible: true, missing: [] });
      toast.success("Certificate issued", result.certificate.id);
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === "not_eligible") {
        await reload();
      }
      toast.reportError(cause, "The certificate could not be issued.");
    } finally {
      setClaiming(false);
    }
  };

  const verifyUrl = data?.certificate
    ? `${window.location.origin}/verify/${data.certificate.id}`
    : "";

  return (
    <AppShell>
      {loading && !data ? (
        <LoadingBlock label="Checking your certificate" />
      ) : error && !data ? (
        <ErrorState error={error} onRetry={() => void reload()} title="This did not load" />
      ) : data ? (
        <div className="cert-layout">
          <nav className="crumbs" aria-label="Breadcrumb">
            <Link to="/">Courses</Link>
            <span aria-hidden="true"> / </span>
            <Link to={`/courses/${data.course.id}`}>{data.course.title}</Link>
            <span aria-hidden="true"> / </span>
            <span aria-current="page">Certificate</span>
          </nav>
          <header className="section-head">
            <h2>{data.course.title} certificate</h2>
            <span className="spread dim">
              {data.progress.modulesComplete} of {data.progress.modulesTotal} modules complete
            </span>
          </header>

          {data.certificate ? (
            <>
              <section className="certificate">
                <p className="cert-brand">The Terraformed Life</p>
                <p className="cert-body" style={{ maxWidth: "32ch" }}>
                  This certifies that
                </p>
                <h1 className="cert-name">{data.certificate.name}</h1>
                <p className="cert-body">
                  has completed {data.course.certificateTitle} at The Terraformed Life:{" "}
                  {data.progress.modulesTotal} modules, every quiz and graded lab, and the assessed
                  capstone{data.course.engine ? `, working with ${data.course.engine}` : ""}.
                </p>
                <dl className="cert-foot">
                  <div>
                    <dt>Issued</dt>
                    <dd>{formatLongDate(data.certificate.issuedAt)}</dd>
                  </div>
                  <div>
                    <dt>Certificate number</dt>
                    <dd>{data.certificate.id}</dd>
                  </div>
                  <div>
                    <dt>Verify at</dt>
                    <dd>{verifyUrl.replace(/^https?:\/\//, "")}</dd>
                  </div>
                </dl>
              </section>

              <div className="row cert-actions">
                <button type="button" className="btn btn-primary" onClick={() => window.print()}>
                  <Icon name="download" />
                  Print or save as PDF
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={async () => setCopied(await copyText(verifyUrl))}
                >
                  <Icon name="copy" />
                  {copied ? "Link copied" : "Copy the verification link"}
                </button>
                <a
                  className="btn"
                  href={`/verify/${data.certificate.id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icon name="external" />
                  Open the public page
                </a>
              </div>
              <p className="hint">
                Anyone can check that link without an account: it shows your name, the issue date
                and whether the certificate is still valid.
              </p>
            </>
          ) : (
            <>
              <section className="panel panel-pad stack">
                <h3>{data.eligible ? "You have earned it" : "What is still to do"}</h3>
                <ProgressBar
                  percent={data.progress.percent}
                  label={`${data.progress.percent}% of ${data.course.title} complete`}
                  done={data.eligible}
                />
                {data.eligible ? (
                  <>
                    <p className="muted">
                      Every module is complete. Claim the certificate and it is issued in your
                      name with a public verification page.
                    </p>
                    <div>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => void claim()}
                        disabled={claiming}
                      >
                        {claiming ? <span className="spinner" /> : <Icon name="award" />}
                        {claiming ? "Issuing…" : "Claim my certificate"}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="muted">
                      The certificate is issued once every module is complete: all lessons read,
                      every quiz passed and every lab green.{" "}
                      {pluralise(data.missing.length, "module")} to go.
                    </p>
                    <ul className="missing-list">
                      {data.missing.map((title) => (
                        <li key={title}>{title}</li>
                      ))}
                    </ul>
                    <div>
                      <Link className="btn btn-primary" to={`/courses/${data.course.id}`}>
                        Back to the course map
                      </Link>
                    </div>
                  </>
                )}
              </section>
            </>
          )}
        </div>
      ) : null}
    </AppShell>
  );
}
